<template>
  <div class="elevation-4">
    <shared-map
      :drawing-tool-cursor="drawingToolCursor"
      @click="handleMapClick"
      @l-map-ready="setLMap"
      @zoom-level-changed="updateZoomLevel"
    >
      <template #map>
        <raster-s-v-g-layer
          v-if="lMap !== null && rasterDatasetStore.hasLoaded"
          :map="lMap"
          :bbox="rasterDatasetStore.metadata?.bboxOuter"
          :grid-bounds="rasterDatasetStore.metadata?.gridBounds"
          :raster-step="rasterDatasetStore.metadata?.spatialResolution"
          :crs="rasterDatasetStore.metadata?.crs"
          :current-zoom="currentZoom"
        />
        <map-marker
          v-for="[id, coord] in Object.entries(timeseriesStore.activeCoords)"
          :key="id"
          :scale-factor="timeseriesStore.highlightedCoordID === id ? 1.5 : 1"
          :lat-lng="coord"
          icon="blue"
          animation="bounce"
          @click="() => handleMarkerClick(id)"
        />
      </template>
      <template #tools>
        <v-tooltip
          :text="t('raster.tooltip.reset_coordinates')"
          bottom
          :open-delay="200"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              :class="'ml-4 delete-button zamg-button-outline-primary'"
              data-testid="map-reset-all-coordinates"
              variant="outlined"
              elevation="2"
              @click="timeseriesStore.resetCoords()"
            >
              <v-icon color="primary">
                mdi-delete-outline
              </v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <v-tooltip
          :text="t('raster.tooltip.select_timeseries_on_map')"
          bottom
          :open-delay="200"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              data-testid="map-add-marker"
              class="ml-4 zamg-button-outline-primary"
              :class="[toolIsActive ? 'active-button' : '']"
              min-width="30px"
              width="30px"
              height="32px"
              @click="() => (toolIsActive = !toolIsActive)"
            >
              <v-icon
                icon="custom:zamgPin"
                color="primary"
              />
            </v-btn>
          </template>
        </v-tooltip>
      </template>
    </shared-map>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, nextTick } from 'vue';
import L, { LatLng, LeafletMouseEvent } from 'leaflet';
import type { Map as LeafletMap } from 'leaflet';
import SharedMap from '@/components/common/map/Map.vue';
import RasterSVGLayer from '@/components/raster/RasterSVGLayer.vue';
import TimeseriesStore from '@/stores/timeseries/timeseries-module';
import MapMarker from '@/components/common/map/Marker.vue';
import RasterDatasetStore from '@/stores/dataset/raster-dataset-module';
import { colors } from '@/plugins/vuetify';
import { COORD_DECIMAL_PRECISION } from '@/util/round';
import mapMarker from '@/assets/map-marker.svg';
import mapMarkerInvalid from '@/assets/map-marker-invalid.svg';
import { LMap } from "@vue-leaflet/vue-leaflet";
import { useI18n } from "vue-i18n";

interface LMapWithMapObject extends InstanceType<typeof LMap> {
  mapObject: L.Map
}

export default defineComponent({
  name: 'TimeseriesMap',
  components: {
    SharedMap,
    RasterSVGLayer,
    MapMarker,
  },
  props: {
    visible: { type: Boolean, required: true },
  },
  setup(props) {
    const { t } = useI18n();

    const timeseriesStore = TimeseriesStore;
    const rasterDatasetStore = RasterDatasetStore;

    const lMap = ref<LMapWithMapObject | null>(null);
    const leafletMap = computed<LeafletMap | null>(() => lMap.value?.mapObject ?? null);
    const currentZoom = ref(0);
    const toolIsActive = ref(false);
    const invalidClick = ref(false);
    const animationTimerId = ref(0);
    const color = colors.primary;

    const setLMap = (map: LMapWithMapObject) => {
      lMap.value = map;
    };

    const cancelToolActive = (e: KeyboardEvent | MouseEvent) => {
      if (('key' in e && e.key === 'Escape') || ('button' in e && e.button === 2)) {
        toolIsActive.value = false;
        e.preventDefault();
      }
    };

    const disableContextMenu = (e: Event) => {
      e.preventDefault();
    };

    watch(toolIsActive, (active) => {
      if (active) {
        document.addEventListener('keyup', cancelToolActive);
        document.addEventListener('mouseup', cancelToolActive);
        document.addEventListener('contextmenu', disableContextMenu);
      } else {
        document.removeEventListener('keyup', cancelToolActive);
        document.removeEventListener('mouseup', cancelToolActive);
        document.removeEventListener('contextmenu', disableContextMenu);
      }
    });

    // When visibility is toggled, invalidate map to reload tiles
    watch(
      () => props.visible,
      async (visible) => {
        if (visible && leafletMap.value) {
          await nextTick();
          leafletMap.value.invalidateSize();
        }
      }
    );

    watch(
      () => timeseriesStore.highlightedCoordID,
      (id) => {
        if (!id) return;
        const coordinate = timeseriesStore.coords[id];
        const map = lMap.value?.mapObject;
        if (!map || !coordinate) return;
        // Only zoom to marker when it is outside of the current map bounds
        if (!map.getBounds().contains(coordinate)) {
          zoomToCoordinate(coordinate);
        }
      }
    );

    // When leaflet map exists, invalidate map to reload tiles
    watch(leafletMap, (newMap) => {
      newMap?.invalidateSize()
    });

    const updateZoomLevel = (zoom: number) => {
      currentZoom.value = zoom;
    };

    const zoomToCoordinate = (coordinate: LatLng) => {
      if (lMap.value) {
        lMap.value.mapObject.setView(coordinate, lMap.value.mapObject.getZoom());
      }
    };

    const handleMarkerClick = (id: string) => {
      timeseriesStore.setHighlightedCoordID(id);
    };

    const handleMapClick = (event: LeafletMouseEvent) => {
      if (event.originalEvent.button !== 0) return; // catch only left click
      if (toolIsActive.value) {
        if (rasterDatasetStore.isCoordInsideBounds(event.latlng)) {
          timeseriesStore.appendCoords(
            new LatLng(
              parseFloat(event.latlng.lat.toFixed(COORD_DECIMAL_PRECISION)),
              parseFloat(event.latlng.lng.toFixed(COORD_DECIMAL_PRECISION))
            )
          );
        } else {
          invalidClick.value = true;
          animationTimerId.value += 1;
          const id = animationTimerId.value;
          setTimeout(() => {
            if (animationTimerId.value === id) invalidClick.value = false;
          }, 300);
        }
      }
    };

    const highlightedCoordID = () => timeseriesStore.highlightedCoordID;

    const drawingToolCursor = computed(() => {
      if (toolIsActive.value && !invalidClick.value) {
        return `url(${mapMarker}) 18 36, auto`;
      }
      if (toolIsActive.value && invalidClick.value) {
        return `url(${mapMarkerInvalid}) 18 36, auto`;
      }
      return '';
    });

    return {
      t,
      timeseriesStore,
      rasterDatasetStore,
      lMap,
      currentZoom,
      toolIsActive,
      invalidClick,
      animationTimerId,
      color,
      drawingToolCursor,
      setLMap,
      updateZoomLevel,
      zoomToCoordinate,
      handleMarkerClick,
      handleMapClick,
      highlightedCoordID,
    };
  },
});
</script>

<style scoped>
.delete-button {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  padding: 0 !important;
}

.active-button {
  background: rgb(var(--v-theme-primary-lighten3)) !important;
}
</style>
